import React, { useState } from 'react';
import { Modal, Descriptions, Tag, Progress, Space, Card, Row, Col, Typography, Button, Form, Input, Checkbox, message, Spin } from 'antd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { RootState } from '../../store';
import {
  CalendarOutlined,
  UserOutlined,
  BankOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { InitialScreeningQuestionnaire } from '../../types/questionnaire';
import {
  getCreditRatingText,
  getInternationalRatingText,
  getTotalAssetsText,
  getIndustryText,
  getGreenProjectStatusText,
  getProjectCategoryText,
  getInvestmentScaleText,
  getGreenStandardComplianceText,
  getIssuanceScaleText,
  getIssuanceTimeText,
  getProductTypeText,
  getRiskFactorText,
  // ⭐ 新增财务状况相关映射
  getDebtToAssetRatioText,
  getNetProfitText,
  getCashFlowStatusText,
  getDefaultRecordText,
  getRegistrationLocationTextExtended,
  getUrgencyLevelTextExtended,
  getEnterpriseTypeTextExtended,
  getTargetMarketTextExtended
} from '../../utils/questionnaireEnumMapping';
import {
  getLargeModalProps,
  getUnifiedModalTitle,
  getCardStyle,
  getCardTitleStyle,
  getPrimaryButtonStyle,
  getSecondaryButtonStyle,
  getHighlightBoxStyle,
} from '../../styles/modalStyles';

const { Text } = Typography;
const { TextArea } = Input;

interface QuestionnaireDetailModalProps {
  visible: boolean;
  loading?: boolean; // 新增loading属性
  questionnaire: InitialScreeningQuestionnaire | null;
  onClose: () => void;
  onReview?: (questionnaireId: string, decision: 'approved' | 'rejected', comments?: string, reasons?: string[]) => void;
  onStartReview?: (questionnaireId: string) => Promise<void>; // 开始审核回调
}

export const QuestionnaireDetailModal: React.FC<QuestionnaireDetailModalProps> = ({
  visible,
  loading = false,
  questionnaire,
  onClose,
  onReview,
  onStartReview,
}) => {
  const { t, i18n } = useTranslation();
  const [reviewForm] = Form.useForm();
  const [isReviewing, setIsReviewing] = useState(false);
  const [reviewDecision, setReviewDecision] = useState<'approved' | 'rejected' | null>(null);
  const [isStartingReview, setIsStartingReview] = useState(false);

  const containsChinese = (text: string) => /[\u4e00-\u9fff]/.test(text);

  const translateOption = (
    group: string,
    value?: string,
    fallback?: (raw: string) => string,
    aliasMap?: Record<string, string>
  ) => {
    const raw = (value || '').trim();
    if (!raw) return '-';

    const optionKey = aliasMap?.[raw] || raw;
    const i18nKey = `questionnaire:options.${group}.${optionKey}`;
    const translated = t(i18nKey);
    if (translated !== i18nKey) {
      return translated;
    }

    if (!fallback) {
      return raw;
    }

    const fallbackValue = fallback(raw);
    if (i18n.language.toLowerCase().startsWith('zh')) {
      return fallbackValue;
    }

    return containsChinese(fallbackValue) ? raw : fallbackValue;
  };

  const ratingAliasMap: Record<string, string> = {
    AAA: 'aaa',
    'AA+': 'aa_plus',
    AA: 'aa',
    'AA-': 'aa_minus',
    'A+_and_below': 'a_plus_and_below',
    no_rating: 'no_rating',
    无评级: 'no_rating',
  };

  const internationalRatingAliasMap: Record<string, string> = {
    no_international_rating: 'no_international_rating',
    investment_grade: 'investment_grade',
    non_investment_grade: 'non_investment_grade',
    无国际评级: 'no_international_rating',
    投资级: 'investment_grade',
    非投资级: 'non_investment_grade',
  };

  const assetScaleAliasMap: Record<string, string> = {
    '50b_to_100b': 'from_50b_to_100b',
    '20b_to_50b': 'from_20b_to_50b',
    '10b_to_20b': 'from_10b_to_20b',
    '5b_to_10b': 'from_5b_to_10b',
    '50亿元以下': 'below_5b',
    '50-100亿元': 'from_5b_to_10b',
    '100-200亿元': 'from_10b_to_20b',
    '200-500亿元': 'from_20b_to_50b',
    '500-1,000亿元': 'from_50b_to_100b',
    '1,000亿元以上': 'above_100b',
  };

  const registrationAliasMap: Record<string, string> = {
    hong_kong: 'overseas',
    macau: 'overseas',
    taiwan: 'overseas',
    other_overseas: 'overseas',
    mainland_china: 'mainland_china',
    overseas: 'overseas',
    中国大陆: 'mainland_china',
    中国境内: 'mainland_china',
    '中国境外（含港澳台）': 'overseas',
  };

  const targetMarketAliasMap: Record<string, string> = {
    domestic_only: 'domestic',
    overseas: 'offshore',
    overseas_only: 'offshore',
    both: 'flexible',
    undecided: 'flexible',
    境内市场: 'domestic',
    '境内市场（银行间/交易所）': 'domestic',
    离岸市场: 'offshore',
    '离岸市场（香港/新加坡等）': 'offshore',
    灵活选择: 'flexible',
    根据条件灵活选择: 'flexible',
  };

  const enterpriseTypeAliasMap: Record<string, string> = {
    其他: 'other',
    民营企业: 'private_enterprise',
    上市公司: 'listed_company',
    中央企业: 'central_soe',
    地方国有企业: 'local_soe',
    外资企业: 'foreign_enterprise',
    合资企业: 'joint_venture',
  };

  const industryAliasMap: Record<string, string> = {
    能源: 'energy',
    基础设施: 'infrastructure',
    金融服务: 'financial_services',
    房地产: 'real_estate',
    制造业: 'manufacturing',
    消费服务: 'consumer_services',
    科技: 'technology',
    其他: 'other',
  };

  const debtRatioAliasMap: Record<string, string> = {
    '60%以下': 'below_60',
    '60%-70%': 'from_60_to_70',
    '70%-80%': 'from_70_to_80',
    '80%以上': 'above_80',
    不清楚: 'unknown',
  };

  const profitScaleAliasMap: Record<string, string> = {
    '10亿元以上': 'above_1b',
    '5-10亿元': 'from_500m_to_1b',
    '1-5亿元': 'from_100m_to_500m',
    '0-1亿元': 'from_0_to_100m',
    亏损: 'loss',
  };

  const cashFlowAliasMap: Record<string, string> = {
    优秀: 'excellent',
    良好: 'good',
    一般: 'fair',
    较差: 'poor',
    不清楚: 'unknown',
  };

  const defaultRecordAliasMap: Record<string, string> = {
    无任何违约记录: 'no_default',
    有违约记录: 'minor_resolved',
    违约已重组: 'default_restructured',
    目前存在违约: 'current_default',
  };

  const greenProjectStatusAliasMap: Record<string, string> = {
    yes: 'clear_project',
    no: 'no_clear_project',
    planning: 'in_planning',
    有明确项目: 'clear_project',
    无明确项目: 'no_clear_project',
    无明确绿色项目: 'no_clear_project',
    规划中: 'in_planning',
  };

  const greenComplianceAliasMap: Record<string, string> = {
    已获得权威机构绿色认证: 'certified',
    符合央行绿色金融目录: 'pboc_compliant',
    符合国际绿色标准: 'international_compliant',
    基本符合需确认: 'basically_compliant',
    不确定是否符合: 'uncertain',
  };

  const investmentScaleAliasMap: Record<string, string> = {
    '100亿元以上': 'above_10b',
    '50-100亿元': 'from_5b_to_10b',
    '20-50亿元': 'from_2b_to_5b',
    '10-20亿元': 'from_1b_to_2b',
    '5-10亿元': 'from_500m_to_1b',
    '5亿元以下': 'below_500m',
  };

  const issuanceScaleAliasMap: Record<string, string> = {
    '50亿元以上': 'above_5b',
    '20-50亿元': 'from_2b_to_5b',
    '10-20亿元': 'from_1b_to_2b',
    '5-10亿元': 'from_500m_to_1b',
    '3-5亿元': 'from_300m_to_500m',
    '3亿元以下': 'below_300m',
  };

  const issuanceTimeAliasMap: Record<string, string> = {
    '1个月内': 'within_1_month',
    '3个月内': 'within_3_months',
    '6个月内': 'within_6_months',
    '1年内': 'within_1_year',
    '1年以上': 'above_1_year',
  };

  const productTypeAliasMap: Record<string, string> = {
    公开发行绿色公司债: 'public_green_bond',
    私募绿色债券: 'private_green_bond',
    绿色中期票据: 'green_mtn',
    绿色银团贷款: 'green_syndicated_loan',
    '绿色ABS/REITs': 'green_abs_reits',
    其他绿色融资产品: 'other_green_product',
  };

  const litigationAliasMap: Record<string, string> = {
    no_litigation: 'no_major_litigation',
    major_litigation: 'major_litigation_high_loss_risk',
    无重大诉讼: 'no_major_litigation',
    有诉讼但金额较小: 'minor_litigation',
    有重大诉讼但胜诉概率高: 'major_litigation_high_win_rate',
    存在重大败诉风险: 'major_litigation_high_loss_risk',
  };

  const envComplianceAliasMap: Record<string, string> = {
    fully_compliant: 'good_compliance',
    minor_issues: 'minor_issues_resolved',
    serious_violations: 'major_violation_risk',
    minor_violations: 'minor_issues_resolved',
    major_violations: 'major_violation_risk',
    '环保合规良好，无违规记录': 'good_compliance',
    有轻微环保问题但已整改: 'minor_issues_resolved',
    存在环保违规正在处理: 'violations_being_handled',
    有重大环保违规风险: 'major_violation_risk',
  };

  const regulatoryAliasMap: Record<string, string> = {
    approved: 'all_approvals_obtained',
    most_approvals_obtained: 'most_approvals_obtained',
    pending_approvals: 'applying_key_approvals',
    in_progress: 'applying_key_approvals',
    under_review: 'applying_key_approvals',
    not_required: 'applying_key_approvals',
    no_approvals_required: 'applying_key_approvals',
    已获得所有必要批文: 'all_approvals_obtained',
    大部分批文已获得: 'most_approvals_obtained',
    正在申请关键批文: 'applying_key_approvals',
    尚未开始审批流程: 'not_started',
  };

  const urgencyAliasMap: Record<string, string> = {
    high: 'very_urgent',
    medium: 'urgent',
    low: 'not_urgent',
    '非常紧急（希望24小时内联系）': 'very_urgent',
    '比较紧急（希望3天内联系）': 'urgent',
    '一般紧急（希望1周内联系）': 'normal',
    '不急（有合适机会再联系）': 'not_urgent',
  };

  // 获取用户信息和权限
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  // 调试信息
  console.log('QuestionnaireDetailModal - 用户信息:', {
    isAuthenticated,
    userRole: user?.role,
    userName: user?.name,
    hasOnReview: !!onReview
  });
  
  // 检查用户是否有审核权限（只有交易所用户可以审核）
  const canReview = isAuthenticated && user?.role === 'exchange' && onReview;

  if (!questionnaire) return null;

  const handleReview = async (decision: 'approved' | 'rejected') => {
    setReviewDecision(decision);
    setIsReviewing(true);
  };

  // 开始审核处理
  const handleStartReview = async () => {
    if (!questionnaire || !onStartReview) return;

    try {
      setIsStartingReview(true);
      await onStartReview(questionnaire._id || questionnaire.id || '');
      message.success(t('screening:detail.startReviewSuccess'));
    } catch (error: any) {
      message.error(error?.data?.error?.message || t('screening:detail.startReviewFailed'));
    } finally {
      setIsStartingReview(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewDecision || !onReview) return;

    try {
      const values = await reviewForm.validateFields();
      await onReview(
        questionnaire._id || questionnaire.id || '',
        reviewDecision,
        values.comments,
        values.reasons
      );

      message.success(reviewDecision === 'approved' ? t('screening:detail.reviewSuccess') : t('screening:detail.reviewRejected'));
      setIsReviewing(false);
      setReviewDecision(null);
      reviewForm.resetFields();
      onClose();
    } catch (error) {
      message.error(t('screening:detail.reviewFailed'));
    }
  };

  const handleCancelReview = () => {
    setIsReviewing(false);
    setReviewDecision(null);
    reviewForm.resetFields();
  };

  const getStatusTag = (status: string) => {
    const statusMap = {
      draft: { color: 'default', textKey: 'questionnaire:status.draft' },
      submitted: { color: 'blue', textKey: 'screening:status.submitted' },
      under_review: { color: 'orange', textKey: 'screening:status.under_review' },
      approved: { color: 'green', textKey: 'screening:status.approved' },
      rejected: { color: 'red', textKey: 'screening:status.rejected' },
      need_supplement: { color: 'orange', textKey: 'screening:status.need_supplement' },
    };
    const config = statusMap[status as keyof typeof statusMap] || { color: 'default', textKey: '' };
    return <Tag color={config.color}>{config.textKey ? t(config.textKey) : status}</Tag>;
  };

  const getRiskLevelTag = (riskLevel: string) => {
    const riskMap = {
      low: { color: 'green', textKey: 'screening:detail.riskLevel.low' },
      medium: { color: 'orange', textKey: 'screening:detail.riskLevel.medium' },
      high: { color: 'red', textKey: 'screening:detail.riskLevel.high' },
    };
    const config = riskMap[riskLevel as keyof typeof riskMap] || { color: 'default', textKey: '' };
    return <Tag color={config.color}>{config.textKey ? t(config.textKey) : riskLevel}</Tag>;
  };

  // 渲染审核表单
  const renderReviewForm = () => (
    <Card
      size="small"
      title={getCardTitleStyle(
        reviewDecision === 'approved' ? <CheckCircleOutlined /> : <CloseCircleOutlined />,
        `${t('screening:review.title')} - ${reviewDecision === 'approved' ? t('screening:review.approve') : t('screening:review.reject')}`
      )}
      style={getHighlightBoxStyle(reviewDecision === 'approved' ? 'success' : 'error')}
      styles={{ body: { padding: '16px' } }}
    >
      <Form form={reviewForm} layout="vertical">
        {reviewDecision === 'rejected' && (
          <Form.Item
            name="reasons"
            label={t('screening:review.rejectionReasons')}
            rules={[{ required: true, message: t('screening:review.rejectionRequired') }]}
          >
            <Checkbox.Group>
              <Space direction="vertical">
                <Checkbox value={t('screening:rejectionReasons.incompleteInfo')}>{t('screening:rejectionReasons.incompleteInfo')}</Checkbox>
                <Checkbox value={t('screening:rejectionReasons.notMeetStandard')}>{t('screening:rejectionReasons.notMeetStandard')}</Checkbox>
                <Checkbox value={t('screening:rejectionReasons.financialIssue')}>{t('screening:rejectionReasons.financialIssue')}</Checkbox>
                <Checkbox value={t('screening:rejectionReasons.environmentalIssue')}>{t('screening:rejectionReasons.environmentalIssue')}</Checkbox>
                <Checkbox value={t('screening:rejectionReasons.legalRisk')}>{t('screening:rejectionReasons.legalRisk')}</Checkbox>
                <Checkbox value={t('screening:rejectionReasons.other')}>{t('screening:rejectionReasons.other')}</Checkbox>
              </Space>
            </Checkbox.Group>
          </Form.Item>
        )}

        <Form.Item
          name="comments"
          label={t('screening:review.commentsOptional')}
        >
          <TextArea
            rows={3}
            placeholder={t('screening:review.commentsPlaceholder')}
          />
        </Form.Item>
      </Form>
    </Card>
  );

  return (
    <Modal
      {...getLargeModalProps()}
      title={getUnifiedModalTitle(
        <BankOutlined />,
        `${t('screening:detail.title')} - ${questionnaire.projectName || t('screening:detail.untitledProject')}`
      )}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose} style={getSecondaryButtonStyle()}>
          {t('screening:detail.close')}
        </Button>,
        // submitted 状态显示"开始审核"按钮
        ...(questionnaire.status === 'submitted' && canReview && onStartReview && !isReviewing
          ? [
              <Button
                key="startReview"
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleStartReview}
                loading={isStartingReview}
                style={getPrimaryButtonStyle('primary')}
              >
                {t('screening:detail.startReview')}
              </Button>,
            ]
          : []),
        // under_review 状态显示"通过/拒绝"按钮
        ...(questionnaire.status === 'under_review' && canReview && !isReviewing
          ? [
              <Button
                key="approve"
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => handleReview('approved')}
                style={getPrimaryButtonStyle('success')}
              >
                {t('screening:detail.reviewApprove')}
              </Button>,
              <Button
                key="reject"
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => handleReview('rejected')}
                style={getPrimaryButtonStyle('danger')}
              >
                {t('screening:detail.reviewReject')}
              </Button>,
            ]
          : []),
        ...(isReviewing && canReview
          ? [
              <Button
                key="cancel"
                onClick={handleCancelReview}
                style={getSecondaryButtonStyle()}
              >
                {t('screening:detail.cancel')}
              </Button>,
              <Button
                key="confirm"
                type="primary"
                onClick={handleSubmitReview}
                style={getPrimaryButtonStyle(
                  reviewDecision === 'approved' ? 'success' : 'danger'
                )}
              >
                {reviewDecision === 'approved' ? t('screening:detail.confirmApprove') : t('screening:detail.confirmReject')}
              </Button>,
            ]
          : []),
      ]}
      className="emerald-modal"
    >
      <Spin spinning={loading} tip={t('screening:detail.loading')}>
        <div style={{ padding: '24px' }}>
          {/* 基本信息概览 */}
        <Card
          size="small"
          style={getCardStyle()}
          title={getCardTitleStyle(<SafetyCertificateOutlined />, t('screening:detail.sections.basicOverview'))}
          styles={{ body: { padding: '16px' } }}
        >
          <Row gutter={[16, 8]}>
            <Col span={8}>
              <Text strong>{t('screening:detail.fields.projectStatus')}：</Text>
              {getStatusTag(questionnaire.status || 'draft')}
            </Col>
            <Col span={8}>
              <Text strong>{t('screening:detail.fields.riskLevel')}：</Text>
              {getRiskLevelTag(questionnaire.riskLevel || 'medium')}
            </Col>
            <Col span={8}>
              <Text strong>{t('screening:detail.fields.aiScore')}：</Text>
                <Progress
                percent={questionnaire.aiScore || 0}
                size="small"
                style={{ width: 80 }}
                format={(percent) => `${percent}`}
              />
            </Col>
          </Row>
          <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text strong>{t('screening:detail.fields.submitTime')}：</Text>
              <Text>{questionnaire.submittedAt ? new Date(questionnaire.submittedAt).toLocaleString() : t('screening:detail.fields.notSubmitted')}</Text>
            </Col>
            <Col span={12}>
              <Text strong>{t('screening:detail.fields.lastUpdate')}：</Text>
              <Text>{questionnaire.updatedAt ? new Date(questionnaire.updatedAt).toLocaleString() : t('screening:detail.fields.noData')}</Text>
            </Col>
          </Row>
        </Card>

        {/* 发行人基本信息 - 始终显示所有字段 */}
        <Card
          size="small"
          style={getCardStyle()}
          title={getCardTitleStyle(<UserOutlined />, t('screening:detail.sections.issuerInfo'))}
          styles={{ body: { padding: '16px' } }}
        >
          <Descriptions size="small" column={2}>
            <Descriptions.Item label={t('screening:detail.fields.issuerName')}>
              {questionnaire.issuerBasicInfo?.issuerName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.guarantorName')}>
              {questionnaire.issuerBasicInfo?.guarantorName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.enterpriseType')}>
              {translateOption(
                'enterpriseType',
                questionnaire.issuerBasicInfo?.enterpriseType || '',
                getEnterpriseTypeTextExtended,
                enterpriseTypeAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.registrationLocation')}>
              {translateOption(
                'registrationType',
                questionnaire.issuerBasicInfo?.registrationLocation || '',
                getRegistrationLocationTextExtended,
                registrationAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.registrationNumber')}>
              {questionnaire.issuerBasicInfo?.registrationNumber || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.hasGuarantor')}>
              {questionnaire.issuerBasicInfo?.hasGuarantor === true ? t('screening:detail.fields.yes') : questionnaire.issuerBasicInfo?.hasGuarantor === false ? t('screening:detail.fields.no') : '-'}
            </Descriptions.Item>
          </Descriptions>

          {/* 发行人评级规模信息 - 始终显示 */}
          <Descriptions size="small" column={2} style={{ marginTop: 16 }}>
            <Descriptions.Item label={t('screening:detail.fields.creditRating')}>
              {translateOption(
                'creditRating',
                questionnaire.issuerRatingScale?.creditRating || '',
                getCreditRatingText,
                ratingAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.internationalRating')}>
              {translateOption(
                'internationalRating',
                questionnaire.issuerRatingScale?.internationalRating || '',
                getInternationalRatingText,
                internationalRatingAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.totalAssets')}>
              {translateOption(
                'assetScale',
                questionnaire.issuerRatingScale?.totalAssets || '',
                getTotalAssetsText,
                assetScaleAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.industry')}>
              {translateOption(
                'industry',
                questionnaire.issuerRatingScale?.industry || '',
                getIndustryText,
                industryAliasMap
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 财务状况 - 始终显示所有字段 */}
        <Card
          size="small"
          style={getCardStyle()}
          title={getCardTitleStyle(<DollarOutlined />, t('screening:detail.sections.financialStatus'))}
          styles={{ body: { padding: '16px' } }}
        >
          <Descriptions size="small" column={2}>
            <Descriptions.Item label={t('screening:detail.fields.debtToAssetRatio')}>
              {translateOption(
                'debtRatio',
                questionnaire.financialStatus?.debtToAssetRatio || '',
                getDebtToAssetRatioText,
                debtRatioAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.netProfit')}>
              {translateOption(
                'profitScale',
                questionnaire.financialStatus?.netProfit || '',
                getNetProfitText,
                profitScaleAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.cashFlowStatus')}>
              {translateOption(
                'cashFlowStatus',
                questionnaire.financialStatus?.cashFlowStatus || '',
                getCashFlowStatusText,
                cashFlowAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.defaultRecord')}>
              {translateOption(
                'defaultRecord',
                questionnaire.financialStatus?.defaultRecord || '',
                getDefaultRecordText,
                defaultRecordAliasMap
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 绿色项目信息 - 始终显示所有字段 */}
        <Card
          size="small"
          style={getCardStyle()}
          title={getCardTitleStyle(<EnvironmentOutlined />, t('screening:detail.sections.greenProjectInfo'))}
          styles={{ body: { padding: '16px' } }}
        >
          <Descriptions size="small" column={2}>
            <Descriptions.Item label={t('screening:detail.fields.hasGreenProject')}>
              {translateOption(
                'greenProjectStatus',
                questionnaire.greenProjectInfo?.hasGreenProject || '',
                getGreenProjectStatusText,
                greenProjectStatusAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.projectCategories')}>
              {questionnaire.greenProjectInfo?.projectCategories?.map((cat: string) =>
                translateOption('greenProjectCategory', cat, getProjectCategoryText)
              ).join(', ') || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.greenStandardCompliance')}>
              {translateOption(
                'greenStandardCompliance',
                questionnaire.greenProjectInfo?.greenStandardCompliance || '',
                getGreenStandardComplianceText,
                greenComplianceAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.projectInvestmentScale')}>
              {translateOption(
                'investmentScale',
                questionnaire.greenProjectInfo?.projectInvestmentScale || '',
                getInvestmentScaleText,
                investmentScaleAliasMap
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 发行计划 - 始终显示所有字段 */}
        <Card
          size="small"
          style={getCardStyle()}
          title={getCardTitleStyle(<TrophyOutlined />, t('screening:detail.sections.issuancePlan'))}
          styles={{ body: { padding: '16px' } }}
        >
          <Descriptions size="small" column={2}>
            <Descriptions.Item label={t('screening:detail.fields.plannedIssuanceScale')}>
              {translateOption(
                'issuanceScale',
                questionnaire.issuancePlan?.plannedIssuanceScale || '',
                getIssuanceScaleText,
                issuanceScaleAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.plannedIssuanceTime')}>
              {translateOption(
                'issuanceTime',
                questionnaire.issuancePlan?.plannedIssuanceTime || '',
                getIssuanceTimeText,
                issuanceTimeAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.productType')}>
              {translateOption(
                'productType',
                questionnaire.issuancePlan?.productType || '',
                getProductTypeText,
                productTypeAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.targetMarket')}>
              {translateOption(
                'targetMarket',
                questionnaire.issuancePlan?.targetMarket || '',
                getTargetMarketTextExtended,
                targetMarketAliasMap
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 风险因素 - 始终显示所有字段 */}
        <Card
          size="small"
          style={getCardStyle()}
          title={getCardTitleStyle(<SafetyCertificateOutlined />, t('screening:detail.sections.riskFactors'))}
          styles={{ body: { padding: '16px' } }}
        >
          <Descriptions size="small" column={2}>
            <Descriptions.Item label={t('screening:detail.fields.majorLitigation')}>
              {translateOption(
                'litigationStatus',
                questionnaire.riskFactors?.majorLitigation || '',
                getRiskFactorText,
                litigationAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.environmentalCompliance')}>
              {translateOption(
                'environmentalCompliance',
                questionnaire.riskFactors?.environmentalCompliance || '',
                getRiskFactorText,
                envComplianceAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.regulatoryApproval')}>
              {translateOption(
                'regulatoryApproval',
                questionnaire.riskFactors?.regulatoryApproval || '',
                getRiskFactorText,
                regulatoryAliasMap
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 联系信息 - 始终显示所有字段 */}
        <Card
          size="small"
          style={getCardStyle()}
          title={getCardTitleStyle(<CalendarOutlined />, t('screening:detail.sections.contactInfo'))}
          styles={{ body: { padding: '16px' } }}
        >
          <Descriptions size="small" column={2}>
            <Descriptions.Item label={t('screening:detail.fields.contactName')}>
              {questionnaire.contactInfo?.contactName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.position')}>
              {questionnaire.contactInfo?.position || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.phone')}>
              {questionnaire.contactInfo?.phone || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.email')}>
              {questionnaire.contactInfo?.email || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.urgencyLevel')}>
              {translateOption(
                'urgencyLevel',
                questionnaire.contactInfo?.urgencyLevel || '',
                getUrgencyLevelTextExtended,
                urgencyAliasMap
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('screening:detail.fields.additionalInfo')} span={2}>
              {questionnaire.additionalInfo ? (
                <div style={{
                  padding: '12px',
                  background: 'rgba(16, 185, 129, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(16, 185, 129, 0.1)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {questionnaire.additionalInfo}
                </div>
              ) : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 审核表单 */}
        {isReviewing && renderReviewForm()}
        </div>
      </Spin>
    </Modal>
  );
};
